import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../utils/api";
import "../styles/private-booking.css";

const initialForm = {
  name: "", email: "", phone: "", pickup: "", dropoff: "",
  pickup_time: "", passengers: 1, bags: 0, flight_number: "", notes: "",
};

export default function PrivateBooking() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState("email");
  const [code, setCode] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/api/private-booking/offers/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        setOffer(data.offer);
        setForm((old) => ({ ...old, pickup: data.offer.pickup, dropoff: data.offer.dropoff }));
      })
      .catch((err) => setError(err.response?.data?.error || "This private booking is unavailable."));
  }, [token]);

  const requestCode = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      await api.post(`/api/private-booking/offers/${encodeURIComponent(token)}/send-code`, { email: form.email });
      setStep("code");
    } catch (err) { setError(err.response?.data?.error || "Could not send the code."); }
    finally { setBusy(false); }
  };

  const verify = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post(`/api/private-booking/offers/${encodeURIComponent(token)}/verify-code`, { code });
      setAuthToken(data.auth_token); setForm((old) => ({ ...old, email: data.offer.email })); setStep("details");
    } catch (err) { setError(err.response?.data?.error || "Verification failed."); }
    finally { setBusy(false); }
  };

  const checkout = async (event) => {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post(`/api/private-booking/offers/${encodeURIComponent(token)}/checkout`, {
        ...form, auth_token: authToken,
      });
      window.location.assign(data.payment_link);
    } catch (err) { setError(err.response?.data?.error || "Could not start secure payment."); setBusy(false); }
  };

  const change = ({ target }) => setForm((old) => ({ ...old, [target.name]: target.value }));
  if (!offer && !error) return <main className="private-booking"><section>Loading your private booking…</section></main>;
  if (!offer) return <main className="private-booking"><section><h1>Booking unavailable</h1><p className="error">{error}</p></section></main>;

  return (
    <main className="private-booking">
      <section className="private-booking-card">
        <header><small>PRIVATE FIXED-FARE BOOKING</small><h1>Your transfer</h1></header>
        <div className="journey"><strong>{offer.pickup}</strong><span>→</span><strong>{offer.dropoff}</strong></div>
        <div className="offer-summary"><span>{offer.vehicle_code.toUpperCase()}</span><b>£{offer.fare_gbp}</b></div>
        {error ? <p className="error" role="alert">{error}</p> : null}

        {step === "email" ? <form onSubmit={requestCode}>
          <label>Email address assigned to this offer<input name="email" type="email" required value={form.email} onChange={change} placeholder={offer.masked_email} /></label>
          <button disabled={busy}>{busy ? "Sending…" : "Send verification code"}</button>
        </form> : null}

        {step === "code" ? <form onSubmit={verify}>
          <label>Six-digit verification code<input inputMode="numeric" pattern="[0-9]{6}" maxLength="6" required value={code} onChange={(e) => setCode(e.target.value)} /></label>
          <button disabled={busy}>{busy ? "Checking…" : "Verify and continue"}</button>
          <button className="link-button" type="button" onClick={() => setStep("email")}>Use a different email</button>
        </form> : null}

        {step === "details" ? <form className="details" onSubmit={checkout}>
          <label>Full name<input name="name" required value={form.name} onChange={change} /></label>
          <label>Phone<input name="phone" type="tel" required value={form.phone} onChange={change} /></label>
          <div className="two"><label>Pickup postcode<input name="pickup" required value={form.pickup} onChange={change} /></label><label>Destination postcode<input name="dropoff" required value={form.dropoff} onChange={change} /></label></div>
          <label>Pickup date and time<input name="pickup_time" type="datetime-local" required value={form.pickup_time} onChange={change} /></label>
          <div className="two"><label>Passengers<input name="passengers" type="number" min="1" max={offer.max_passengers} required value={form.passengers} onChange={change} /></label><label>Bags<input name="bags" type="number" min="0" max={offer.max_bags} required value={form.bags} onChange={change} /></label></div>
          <label>Flight number (optional)<input name="flight_number" value={form.flight_number} onChange={change} /></label>
          <label>Notes (optional)<textarea name="notes" maxLength="1000" value={form.notes} onChange={change} /></label>
          <button disabled={busy}>{busy ? "Opening secure payment…" : `Book now — £${offer.fare_gbp}`}</button>
          <p className="secure">Secure card payment is completed on Stripe.</p>
        </form> : null}
      </section>
    </main>
  );
}
